// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface UseUnsavedChangesOptions {
  hasUnsavedChanges: boolean;
  onSave?: () => Promise<void> | void;
  message?: string;
}

interface UnsavedChangesState {
  showModal: boolean;
  pendingNavigation: string | null;
}

export const useUnsavedChanges = ({
  hasUnsavedChanges,
  onSave,
  message = 'You have unsaved changes. Are you sure you want to leave without saving?'
}: UseUnsavedChangesOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<UnsavedChangesState>({
    showModal: false,
    pendingNavigation: null
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Keep track of the original navigate function
  const originalNavigate = useRef(navigate);

  useEffect(() => {
    // Override the navigate function when there are unsaved changes
    if (hasUnsavedChanges) {
      const interceptedNavigate = (to: string | number, options?: any) => {
        if (typeof to === 'string') {
          // Check if we're navigating to a different route
          if (to !== location.pathname + location.search + location.hash) {
            setState({
              showModal: true,
              pendingNavigation: to
            });
            return;
          }
        } else if (typeof to === 'number') {
          // Handle back/forward navigation
          setState({
            showModal: true,
            pendingNavigation: 'back'
          });
          return;
        }
        // Allow navigation for same route
        originalNavigate.current(to as any, options);
      };

      // Replace the navigate function temporarily
      Object.assign(navigate, interceptedNavigate);
    } else {
      // Restore original navigate function when no unsaved changes
      Object.assign(navigate, originalNavigate.current);
    }

    // Cleanup function to restore original navigate
    return () => {
      Object.assign(navigate, originalNavigate.current);
    };
  }, [hasUnsavedChanges, location, navigate]);

  // Handle browser back/forward button and page refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', location.pathname + location.search + location.hash);
        setState({
          showModal: true,
          pendingNavigation: 'back'
        });
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      
      // Push a state to handle back button
      window.history.pushState(null, '', location.pathname + location.search + location.hash);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, message, location]);

  const handleConfirmLeave = async () => {
    setState({ showModal: false, pendingNavigation: null });
    
    if (state.pendingNavigation) {
      if (state.pendingNavigation === 'back') {
        window.history.back();
      } else {
        originalNavigate.current(state.pendingNavigation);
      }
    }
  };

  const handleSaveAndLeave = async () => {
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        setState({ showModal: false, pendingNavigation: null });
        
        if (state.pendingNavigation) {
          if (state.pendingNavigation === 'back') {
            window.history.back();
          } else {
            originalNavigate.current(state.pendingNavigation);
          }
        }
      } catch (error) {
        console.error('Failed to save:', error);
        // Keep modal open on save error
      } finally {
        setIsSaving(false);
      }
    } else {
      handleConfirmLeave();
    }
  };

  const handleCancelLeave = () => {
    setState({ showModal: false, pendingNavigation: null });
  };

  return {
    showUnsavedChangesModal: state.showModal,
    isSaving,
    confirmLeave: handleConfirmLeave,
    saveAndLeave: handleSaveAndLeave,
    cancelLeave: handleCancelLeave,
    message
  };
};
